import React, { useCallback, useContext, useMemo, useRef } from "react";
import { useModal } from "react-modal-hook";
import { buildGuestWorkflowURL } from "common/utils/url";
import {
  AccessControlEntityType,
  AccessControlPolicy,
  useSyncWorkflowCollaboratorsMutation,
  useWorkflowCollaboratorsQuery,
  useWorkflowStepCollaboratorsQuery,
} from "../../generated/graphql";
import { useStatusToasts } from "../../hooks/useStatusToasts";
import { track } from "../../utils/tracking";
import { AuthContext } from "../Authorization/AuthContext";
import { CommonModal } from "../Common/CommonModal";
import { Loading } from "../Common/Icons/Loading";
import Modal from "../Common/Modal";
import { Collaborator, PermissionOption, ShareForm } from "../Common/ShareForm";

export type ShareableEntityType = "workflow" | "workflowStep";

export interface ShareWorkflowModalProps {
  entityType: ShareableEntityType;
  entityId: string;
  workflowId: string;
  entityName: string;
  onClose: () => void;
  clipboard?: Pick<Clipboard, "writeText">;
  onInvite?: (collaborators: Collaborator[]) => void;
  onRemove?: (collaboratorId: string) => void;
}

const WORKFLOW_PERMISSION_OPTIONS: PermissionOption[] = [
  { value: "createResource", label: "Can create" },
  { value: "updateResource", label: "Can update" },
  { value: "viewResource", label: "Can view" },
];

const getModalTitle = (entityType: ShareableEntityType): string => {
  switch (entityType) {
    case "workflow":
      return "Share workflow";
    case "workflowStep":
      return "Share step";
    default:
      return "Share";
  }
};

const getInfoText = (entityType: ShareableEntityType) => {
  const entityLabel = entityType === "workflow" ? "workflow" : "step";
  return {
    text: `All Forerunner team members can view this ${entityLabel}.`,
    linkText: "Add a team member",
    linkHref: "/settings/users/team",
  };
};

interface CollaboratorWithEntityInfo extends Collaborator {
  accessControlId: string;
  entityType: AccessControlEntityType;
}

export const ShareWorkflowModalContent = ({
  entityType,
  entityId,
  workflowId,
  entityName,
  onClose,
  clipboard = navigator.clipboard,
}: Omit<ShareWorkflowModalProps, "onInvite" | "onRemove">) => {
  const { account } = useContext(AuthContext);
  const { addSuccessToast, addErrorToast } = useStatusToasts();

  const { data: workflowData, loading: workflowLoading } =
    useWorkflowCollaboratorsQuery({
      variables: { workflowId: entityId },
      skip: entityType !== "workflow",
      fetchPolicy: "no-cache",
      notifyOnNetworkStatusChange: true,
    });

  const { data: stepData, loading: stepLoading } =
    useWorkflowStepCollaboratorsQuery({
      variables: { workflowStepId: entityId },
      skip: entityType !== "workflowStep",
      fetchPolicy: "no-cache",
      notifyOnNetworkStatusChange: true,
    });

  const isLoading = entityType === "workflow" ? workflowLoading : stepLoading;

  const [syncCollaborators, { loading: isSubmitting }] =
    useSyncWorkflowCollaboratorsMutation();

  const serverCollaborators = useMemo<CollaboratorWithEntityInfo[]>(() => {
    const collaborators =
      entityType === "workflow" ?
        workflowData?.workflowCollaborators
      : stepData?.workflowStepCollaborators;

    if (!collaborators) return [];

    const permissionRank: Record<string, number> = {
      createResource: 3,
      updateResource: 2,
      viewResource: 1,
    };

    const collaboratorMap = new Map<string, CollaboratorWithEntityInfo>();

    collaborators.forEach(collab => {
      const user = collab.user ?? collab.publicUser;
      const name =
        user ? `${user.firstName} ${user.lastName}`.trim() : "Unknown";
      const description = user?.email;

      const existing = collaboratorMap.get(collab.entityId);
      const currentRank = permissionRank[collab.policy] || 0;
      const existingRank =
        existing ? permissionRank[existing.permission] || 0 : -1;

      if (currentRank > existingRank) {
        collaboratorMap.set(collab.entityId, {
          id: collab.entityId,
          name: name || description || "Unknown",
          description,
          permission: collab.policy,
          accessControlId: collab.id,
          entityType: collab.entityType,
        });
      }
    });

    return Array.from(collaboratorMap.values()).sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }, [
    entityType,
    workflowData?.workflowCollaborators,
    stepData?.workflowStepCollaborators,
  ]);

  const currentCollaboratorsRef = useRef<Collaborator[]>([]);

  const handleStateChange = useCallback((collaborators: Collaborator[]) => {
    currentCollaboratorsRef.current = collaborators;
  }, []);

  const handleCopyLink = useCallback(async () => {
    const url = buildGuestWorkflowURL({
      subdomain: account!.subdomain,
      workflowId,
    });
    await clipboard.writeText(url);
    addSuccessToast("Link copied to clipboard");
  }, [account, workflowId, clipboard, addSuccessToast]);

  const handleSubmit = useCallback(async () => {
    try {
      await syncCollaborators({
        variables: {
          data: {
            workflowId,
            workflowStepId:
              entityType === "workflowStep" ? entityId : undefined,
            collaborators: currentCollaboratorsRef.current.map(c => ({
              entityId: c.id,
              entityType: AccessControlEntityType.PUBLIC_USER,
              policy: c.permission as AccessControlPolicy,
            })),
          },
        },
      });

      const eventName =
        entityType === "workflow" ? "Shared Workflow" : "Shared Workflow Step";
      track(eventName, {
        workflowId,
        workflowStepId: entityType === "workflowStep" ? entityId : undefined,
      });

      const entityLabel = entityType === "workflow" ? "workflow" : "step";
      addSuccessToast(
        `The collaborators for this ${entityLabel} have been updated successfully!`,
      );

      onClose();
    } catch {
      addErrorToast("Failed to update collaborators");
    }
  }, [
    workflowId,
    entityType,
    entityId,
    syncCollaborators,
    addSuccessToast,
    addErrorToast,
    onClose,
  ]);

  return (
    <CommonModal
      title={getModalTitle(entityType)}
      subtitle={entityName}
      size="large"
      onCancel={onClose}
      primaryButtonText={isSubmitting ? "Saving..." : "Done"}
      onSubmit={handleSubmit}
      disabled={isSubmitting || isLoading}
      secondaryButtons={[
        {
          text: "Copy link",
          onClick: handleCopyLink,
        },
      ]}
      content={
        isLoading ?
          <Loading size="medium" />
        : <ShareForm
            searchPlaceholder="Add email address"
            sectionLabel="Who has access"
            infoText={getInfoText(entityType)}
            permissionOptions={WORKFLOW_PERMISSION_OPTIONS}
            initialCollaborators={serverCollaborators}
            onStateChange={handleStateChange}
            buttonText="Invite"
            showRemoveOption={true}
            includeUsers={false}
            includePublicUsers={true}
          />
      }
    />
  );
};

export const useShareWorkflowModal = ({
  entityType,
  entityId,
  workflowId,
  entityName,
  clipboard = navigator.clipboard,
}: Omit<ShareWorkflowModalProps, "onClose" | "onInvite" | "onRemove">) => {
  const [showModal, hideModal] = useModal(
    () => (
      <Modal onRequestClose={hideModal}>
        <ShareWorkflowModalContent
          entityType={entityType}
          entityId={entityId}
          workflowId={workflowId}
          entityName={entityName}
          onClose={hideModal}
          clipboard={clipboard}
        />
      </Modal>
    ),
    [entityType, entityId, workflowId, entityName, clipboard],
  );

  return [showModal, hideModal] as const;
};
